import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { setAuthCookie, removeAuthCookie } from '@/lib/utils/cookie';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'EDITOR' | 'AUTHOR' | 'CONTRIBUTOR' | 'READER' | 'USER';
  avatar?: string | null;
  /** Comentarios verificados: true si el usuario confirmó su email. */
  emailVerified?: boolean;
}

export interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;

  // Computed property
  isAuthenticated: boolean;

  // Actions
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  logout: () => void;
  setError: (error: string) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  hydrate: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isLoading: false,
      error: null,
      isAuthenticated: false,

      // Actions
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setToken: (token) => {
        if (token) {
          setAuthCookie(token);
        }
        set({ token, isAuthenticated: !!token });
      },
      logout: () => {
        removeAuthCookie();
        set({ user: null, token: null, isAuthenticated: false, error: null });
      },
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      setLoading: (loading) => set({ isLoading: loading }),
      hydrate: () => {
        const state = get();
        const isAuth = !!(state.user && state.token);
        set({ isAuthenticated: isAuth });
        if (state.token) setAuthCookie(state.token);
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage((): StateStorage => {
        if (typeof window !== 'undefined') {
          return window.localStorage;
        }
        return {
          getItem: () => null,
          setItem: () => {},
          removeItem: () => {},
        };
      }),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          const isAuth = !!(state.user && state.token);
          state.isAuthenticated = isAuth;
          if (state.token) setAuthCookie(state.token);
        }
      },
    }
  )
);
